import { CalendarIcon, ChevronLeftIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";
import CandidateModel from "../../../api/models/CandidateModel";
import StudentModel from "../../../api/models/StudentModel";
import { classNames } from "../../../helpers";
import CandidateSubmissionStatus from "../../../components/candidate-submission-status/CandidateSubmissionStatus";

type CandidateTableProps = {
  students: StudentModel[];
  onScheduleStudent: (student: StudentModel) => void;
  candidates: CandidateModel[];
  onCandidateClick: (candidate: CandidateModel) => void;
};

export default function CandidateTable(props: CandidateTableProps) {
  const { students, onScheduleStudent, candidates, onCandidateClick } = props;

  const LocalBasePath = "page.liaison.dashboard.candidates";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div>
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-10 hidden">
                <tr>
                  <th scope="col" className="py-3.5 ps-4 pe-3 sm:ps-6">
                    <span className="sr-only">Candidate</span>
                  </th>
                  <th scope="col" className="px-3 py-3.5">
                    <span className="sr-only">status</span>
                  </th>

                  <th scope="col" className="relative py-3.5 ps-3 pe-4 sm:pe-6">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody data-cy="tableBody">
                {students.map(
                  (
                    student: StudentModel,
                    index: number,
                    studentList: StudentModel[]
                  ) => (
                    <tr
                      key={`CandidateInfoTable_${index}_${student.id}`}
                      className={" border-b cursor-pointer hover:bg-gray-50"}
                    >
                      <td
                        className={classNames(
                          "truncate py-4 ps-4 pe-3 text-sm sm:ps-6",
                          index === studentList.length - 1 && "pb-8"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {student.fullName}
                            </div>
                            <div className="text-gray-500">
                              {student.sponsorship?.sponsor?.name && (
                                <span className="flex justify-start gap-1">
                                  <span>
                                    {student.sponsorship.sponsor.name}
                                  </span>
                                  <span>{t("backSlash")}</span>
                                  <span>{student.sponsorship.id}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        className={classNames(
                          "hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500",
                          index === studentList.length - 1 && "pb-8"
                        )}
                      >
                        {t("notScheduledYet")}
                      </td>
                      <td className="md:hidden" colSpan={0}></td>
                      <td className="relative px-5  py-4 text-sm text-gray-500">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            data-cy="scheduleStudent"
                            onClick={() => onScheduleStudent(student)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
                          >
                            <div className="flex items-center justify-start gap-2">
                              <CalendarIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                              <span className="hidden md:flex">
                                {t("scheduleReader")}
                              </span>
                            </div>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
                {candidates.map((candidate, index) => {
                  return (
                    <tr
                      key={`CandidateInfoTable_${index}_${candidate.id}`}
                      className={" border-b cursor-pointer hover:bg-gray-50"}
                      onClick={() => onCandidateClick(candidate)}
                      data-cy="editCandidateModal"
                    >
                      <td className="whitespace-nowrap py-4 ps-4 pe-3 text-sm sm:ps-6">
                        <div className="flex items-center">
                          <div className="">
                            <div className="font-medium text-gray-900">
                              {candidate.name}
                            </div>
                            <div className="text-gray-500">
                              {candidate.sponsor?.name && (
                                <span className="flex justify-start gap-1">
                                  <span>{candidate.sponsor.name} </span>
                                  <span>{t("backSlash")}</span>
                                  <span>{candidate.sponsorship?.id}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="md:whitespace-nowrap min-w-[8.5rem] px-3 py-4 text-sm text-gray-500 flex flex-col justify-start">
                        <CandidateSubmissionStatus status={candidate.status} />
                        <div className="hidden md:block text-gray-500">
                          {t("lastUpdate", {
                            date: candidate.LastSubmissionUpdate?.format(
                              "D/M/YYYY"
                            ),
                          })}
                        </div>
                      </td>
                      <td className="py-4 rtl:pl-6 ltr:pr-6">
                        <div className="flex justify-end">
                          <ChevronLeftIcon
                            className="h-4 w-4 text-gray-400 ltr:rotate-180"
                            aria-hidden="true"
                          />
                        </div>
                        <span className="sr-only">Show info</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
